import React, { useMemo } from "react";
import { getTransform } from "../../../utils/getTransform";
import {
  aspectFromSize,
  thumbSizeFromAspect,
  useImageNaturalSize,
} from "../lib/toolPrepImageLayout";
import { useLiveSourceCardStats } from "../hooks/useLiveSourceCardStats";
import RoundLoader from "../../../components/ui/RoundLoader";
import "../layout/resize/Editor/ResizeModeCards.css";
import "./previewBg/ToolPrepPreviewBgCards.css";
import ToolPrepUploadDropzone from "./Popup/ToolPrepUploadDropzone";
import WatermarkThumbMarks from "../watermark/Popup/WatermarkThumbMarks";
import {
  ToolPrepOriginalOverlays,
} from "./ToolPrepSourceIdentity";
import ToolPrepRemoveBgBlock from "./Popup/ToolPrepRemoveBgBlock";
import "./ToolPrepSourceCards.css";

/** Fallback until natural size loads (matches Rotate / Expand). */
const FALLBACK_ASPECT = 3 / 4;
const THUMB_WIDTH_PX = 140;

/**
 * Shared source cards for Resize / Recolor / Watermark tools.
 *
 * Layout when filled:
 * 1. Original | Preview (Replace hover + Remove X on Original; footnotes)
 * 2. Optional Remove Background toggle
 */
const ToolPrepSourceCards = ({
  selectedKey = "original",
  onSelect = () => {},
  originalImageUrl = null,
  previewImageUrl = null,
/**
   * Target output aspect (w/h). Preview card keeps the upload frame;
   * output is fitted/centered inside it so shape change is visual.
   */
  previewAspectRatio = null,
  removeBgActive = false,
  cutoutPending = false,
  onUploadClick = () => {},
  onRemove = null,
  watermark = null,
  /** Override thumb mark compositor (Sign PDF → SignPdfThumbMarks). */
  MarksComponent = WatermarkThumbMarks,
  /** image | pdf | word — empty dropzone kind */
  uploadKind = "image",
  uploadLabel = null,
  uploadHint = null,
  onDropFiles = null,
  /** When true, empty state is omitted (parent owns upload UI) */
  compareOnly = false,
  /** 0–100 while preparing a new upload */
  uploadProgress = null,
  /** Source identity — storage awareness */
  sourceName = null,
  sourceSizeLabel = null,
  /** e.g. "3 pages" — shown under both cards */
  sourceMetaExtra = null,
  /** Disable Replace/Remove (busy / exporting) */
  actionsDisabled = false,
  /**
   * Optional Remove Background controls shown under Original | Preview.
   * @type {null | { active?: boolean, onToggle?: Function, disabled?: boolean, locked?: boolean, bio?: string, error?: string|null, label?: string }}
   */
  removeBgControls = null,
}) => {
  const originalNatural = useImageNaturalSize(originalImageUrl);
  const previewNatural = useImageNaturalSize(previewImageUrl);

  const originalAspect = useMemo(
    () =>
      aspectFromSize(
        originalNatural.width,
        originalNatural.height,
        FALLBACK_ASPECT
      ),
    [originalNatural]
  );

  /** Both cards share the upload frame; output is fitted inside Preview. */
  const frameThumbSize = useMemo(
    () => thumbSizeFromAspect(originalAspect, THUMB_WIDTH_PX, FALLBACK_ASPECT),
    [originalAspect]
  );

  const outputAspect = useMemo(() => {
    if (previewNatural.width > 0 && previewNatural.height > 0) {
      return aspectFromSize(
        previewNatural.width,
        previewNatural.height,
        FALLBACK_ASPECT
      );
    }
    if (previewAspectRatio != null && Number(previewAspectRatio) > 0) {
      return Number(previewAspectRatio);
    }
    return originalAspect;
  }, [previewNatural, previewAspectRatio, originalAspect]);

  const originalFit = useMemo(
    () =>
      getTransform(
        originalNatural.width,
        originalNatural.height,
        frameThumbSize.width,
        frameThumbSize.height,
        "fit"
      ),
    [originalNatural, frameThumbSize]
  );

  const previewFit = useMemo(() => {
    if (previewNatural.width > 0 && previewNatural.height > 0) {
      return getTransform(
        previewNatural.width,
        previewNatural.height,
        frameThumbSize.width,
        frameThumbSize.height,
        "fit"
      );
    }
    // Virtual output size for target-aspect placeholder (centered in upload frame).
    const unit = 1000;
    return getTransform(
      Math.max(1, outputAspect * unit),
      unit,
      frameThumbSize.width,
      frameThumbSize.height,
      "fit"
    );
  }, [previewNatural, frameThumbSize, outputAspect]);

  const frameThumbStyle = {
    width: frameThumbSize.width,
    height: frameThumbSize.height,
    aspectRatio: `${frameThumbSize.aspect}`,
  };

  const showCutoutPreview = Boolean(removeBgActive && previewImageUrl);
  const previewLoading = Boolean(cutoutPending);
  const hasTargetPreviewShape =
    previewAspectRatio != null && Number(previewAspectRatio) > 0;
  const showOutputMatte =
    !previewImageUrl && hasTargetPreviewShape && !previewLoading;
  /** Don't mirror the upload into Preview when we know a different output shape. */
  const previewPlaceholderSrc =
    previewImageUrl ||
    (hasTargetPreviewShape ? null : originalImageUrl);

  const { originalSizeFootnote, previewSizeFootnote } = useLiveSourceCardStats({
    originalImageUrl,
    previewImageUrl: previewImageUrl || originalImageUrl,
    originalSizeLabel: sourceSizeLabel,
    watermark,
    enabled: Boolean(originalImageUrl) && uploadProgress == null,
  });

  const originalFootnote = [originalSizeFootnote, sourceMetaExtra]
    .filter(Boolean)
    .join(" · ");
  const previewFootnote = [previewSizeFootnote, sourceMetaExtra]
    .filter(Boolean)
    .join(" · ");

  const renderThumbImage = (src, fit) => {
    if (!src) return null;
    const hasTransform = fit.width > 0;
    return (
      <img
        src={src}
        alt=""
        className={
          hasTransform
            ? "resize-mode-thumb__img"
            : "resize-mode-thumb__img resize-mode-thumb__img--fallback"
        }
        style={
          hasTransform
            ? {
                width: fit.width,
                height: fit.height,
                left: fit.x,
                top: fit.y,
              }
            : undefined
        }
        draggable={false}
      />
    );
  };

  const handleOriginalClick = () => {
    if (!originalImageUrl) {
      onUploadClick();
      return;
    }
    onSelect("original");
  };

  // Preparing upload — keep the dashed zone with progress (incl. Replace)
  if (uploadProgress != null) {
    return (
      <div
        className="tool-prep-source-cards tool-prep-source-cards--empty"
        key="progress"
      >
        <ToolPrepUploadDropzone
          kind={uploadKind === "word" ? "word" : uploadKind === "pdf" ? "pdf" : "image"}
          label={uploadLabel}
          hint={uploadHint}
          progress={uploadProgress}
        />
      </div>
    );
  }

  // Step 1 — single upload placeholder
  if (!originalImageUrl) {
    if (compareOnly) return null;
    return (
      <div
        className="tool-prep-source-cards tool-prep-source-cards--empty"
        key="empty"
      >
        <ToolPrepUploadDropzone
          kind={uploadKind === "word" ? "word" : uploadKind === "pdf" ? "pdf" : "image"}
          label={uploadLabel}
          hint={uploadHint}
          onClick={onUploadClick}
          onFiles={onDropFiles}
        />
      </div>
    );
  }

  // Step 2 — Original | Preview (+ footnotes) → optional Remove Background.
  // Replace and Remove stay on Original after a result so the source can change.
  return (
    <div
      className={`tool-prep-source-cards tool-prep-source-cards--filled${
        compareOnly ? " tool-prep-source-cards--compare" : ""
      }`}
      key="filled"
    >
      <div className="resize-mode-grid tool-prep-source-cards__pair">
        <div
          className={`resize-mode-card tool-prep-source-cards__card${
            selectedKey === "original" ? " resize-mode-card--active" : ""
          }`}
        >
          <div className="tool-prep-source-cards__frame-wrap">
            <button
              type="button"
              className="tool-prep-source-cards__hit"
              onClick={handleOriginalClick}
              aria-pressed={selectedKey === "original"}
              aria-label="Show original"
            >
              <div className="resize-mode-frame">
                <div className="resize-mode-thumb" style={frameThumbStyle}>
                  {renderThumbImage(originalImageUrl, originalFit)}
                </div>
              </div>
            </button>
            <ToolPrepOriginalOverlays
              onReplace={onUploadClick}
              onRemove={onRemove}
              disabled={actionsDisabled}
            />
          </div>
          <div className="tool-prep-source-cards__caption">
            <button
              type="button"
              className="tool-prep-source-cards__label-btn"
              onClick={handleOriginalClick}
            >
              <span className="resize-mode-label">Original</span>
            </button>
            {originalFootnote ? (
              <span className="tool-prep-source-cards__card-stat">
                {originalFootnote}
              </span>
            ) : null}
          </div>
        </div>

        <button
          type="button"
          className={`resize-mode-card${
            selectedKey === "preview" ? " resize-mode-card--active" : ""
          }`}
          onClick={() => {
            if (previewImageUrl || previewLoading || originalImageUrl) {
              onSelect("preview");
            }
          }}
          aria-pressed={selectedKey === "preview"}
          aria-label="Show preview"
          disabled={!previewImageUrl && !previewLoading && !originalImageUrl}
        >
          <div className="resize-mode-frame">
            <div
              className={`resize-mode-thumb${
                showCutoutPreview ? " tpp-bg-thumb--transparent" : ""
              }${showOutputMatte ? " tool-prep-source-cards__thumb--framed" : ""}`}
              style={frameThumbStyle}
            >
              {previewLoading ? (
                <div className="tool-prep-source-cards__empty">
                  <RoundLoader size="sm" />
                </div>
              ) : previewPlaceholderSrc ? (
                <>
                  {renderThumbImage(previewPlaceholderSrc, previewFit)}
                  {watermark && MarksComponent ? (
                    <MarksComponent
                      mark={watermark}
                      watermark={watermark}
                      width={frameThumbSize.width}
                      height={frameThumbSize.height}
                    />
                  ) : null}
                </>
              ) : showOutputMatte ? (
                <span
                  className="tool-prep-source-cards__output-matte"
                  style={{
                    width: previewFit.width,
                    height: previewFit.height,
                    left: previewFit.x,
                    top: previewFit.y,
                  }}
                  aria-hidden="true"
                />
              ) : (
                <div className="tool-prep-source-cards__empty tool-prep-source-cards__empty--muted">
                  <span className="tool-prep-source-cards__empty-text">
                    Preview
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="tool-prep-source-cards__caption">
            <span className="resize-mode-label">Preview</span>
            {previewFootnote ? (
              <span className="tool-prep-source-cards__card-stat">
                {previewFootnote}
              </span>
            ) : null}
          </div>
        </button>
      </div>

      {removeBgControls ? (
        <ToolPrepRemoveBgBlock
          inScroll
          active={Boolean(removeBgControls.active)}
          onToggle={removeBgControls.onToggle}
          disabled={Boolean(removeBgControls.disabled)}
          locked={Boolean(removeBgControls.locked)}
          bio={removeBgControls.bio || ""}
          error={removeBgControls.error || null}
          label={removeBgControls.label || "Remove Background"}
        />
      ) : null}
    </div>
  );
};

export default ToolPrepSourceCards;
